import { calculateTotal, type QuoteFormData, type QuoteItem } from '@/components/quotes/QuoteForm';
import { onlyDigits } from '@/lib/documentUtils';
import { buildNewClientColumns, resolveClientAddressLine } from '@/lib/clientAddress';
import {
  catalogProductColumnsFromQuoteItem,
  quoteItemFinanceEntryId,
  quoteItemUnitCost,
} from '@/lib/quoteItems';
import { buildQuoteTotalsColumns, quoteItemLineTotal } from '@/lib/quoteTotals';
import { buildQuotePaymentColumns } from '@/lib/quotePaymentInfo';
import { buildQuotePdfTemplateColumns } from '@/lib/quotePdfTemplate';
import { buildQuoteWarrantyColumns } from '@/lib/quoteWarranty';
import { buildQuoteHeadingColumns } from '@/lib/quoteHeading';
import { buildQuoteExtraFieldsColumns } from '@/lib/quoteExtraFields';
import type {
  OfflineClientData,
  OfflineCatalogItemData,
  OfflineQuoteData,
  OfflineSaveOptions,
} from '@/hooks/useOfflineSync';

/**
 * Everything {@link persistQuoteOffline} needs from its caller: the owning
 * user/company and the three offline-save callbacks from `useOfflineSync`. The
 * callback types come straight from the hook, so they stay in sync with it.
 */
export interface PersistQuoteOfflineCtx {
  userId: string;
  companyId: string | null;
  countTowardQuoteQuota: boolean;
  saveClientOffline: (data: OfflineClientData, options?: OfflineSaveOptions) => string;
  saveCatalogItemOffline: (data: OfflineCatalogItemData, options?: OfflineSaveOptions) => string;
  saveQuoteOffline: (
    data: OfflineQuoteData & { generalPhotos?: any[] },
    options?: OfflineSaveOptions,
  ) => string;
}

/**
 * Queues a brand-new quote (plus any marked client/catalog items) to the offline
 * sync queue and local cache, returning its temporary offline id.
 *
 * Single source of truth for the offline creation of a NEW quote: shared by the
 * "Salvar" and "Baixar PDF" offline paths in NewQuotePage so both persist the
 * exact same payload. Only valid for logged-in users creating a new quote (no
 * server round-trip). Pass `{ silent: true }` to suppress the "salvo localmente"
 * toast when the caller shows its own confirmation.
 */
export function persistQuoteOffline(
  data: QuoteFormData,
  ctx: PersistQuoteOfflineCtx,
  opts?: OfflineSaveOptions,
): string {
  const {
    userId,
    companyId,
    countTowardQuoteQuota,
    saveClientOffline,
    saveCatalogItemOffline,
    saveQuoteOffline,
  } = ctx;

  const clientName = data.clientName.trim();
  const clientPhone = data.newClientPhone?.trim() || null;
  const clientAddress = resolveClientAddressLine(data);
  const clientDocument = onlyDigits(data.newClientDocument || '') || null;
  const items: QuoteItem[] = Array.isArray(data.items) ? data.items : [];
  const finalTotal = calculateTotal({ ...data, items });

  // Save client to offline queue first if marked so we have its temporary ID.
  // company_id must be set: team-account RLS rejects inserts whose company_id
  // the user can't write to (null included), so the sync would fail otherwise.
  let offlineClientId: string | null = null;
  if (data.saveNewClient && !data.selectedClient && clientName) {
    offlineClientId = saveClientOffline({
      // Mesmo builder dos saves online (src/lib/clientAddress.ts) — o executor
      // da fila já grava email/CEP/endereço estruturado, então basta entregá-los.
      client: buildNewClientColumns(data),
      userId,
      companyId,
    }, { silent: true });
  }

  // Save catalog items offline if marked and map their temp IDs.
  const catalogItemOfflineMap = new Map<string, string>(); // item.id -> offlineProductId
  for (const item of items) {
    if (item.saveToCategory && item.name?.trim()) {
      const offlineProductId = saveCatalogItemOffline({
        item: {
          name: item.name,
          price: item.unit_price,
          // Custo do item, quando o usuário informou junto do "salvar no
          // catálogo". Payload por LISTA EXPLÍCITA de campos: sem esta linha o
          // produto sobe SEM custo quando a rede volta — toast de sucesso, sem
          // erro de RLS, custo em branco (armadilha #33/#62). E `null` nunca
          // pode virar 0, que significaria "de graça" (armadilha #61).
          cost: quoteItemUnitCost(item),
          // O resto das colunas de cadastro vem do builder único — este payload
          // era o mais pobre dos cinco (nem categoria, nem código interno, nem
          // estoque, nem classificação fiscal). Variações e materiais ficam de
          // fora: são online-only (#30c/#65).
          ...catalogProductColumnsFromQuoteItem(item),
          image_url: item.image_url || null,
        },
        userId,
        companyId,
      }, { silent: true });
      catalogItemOfflineMap.set(item.id, offlineProductId);
    }
  }

  const quoteData = {
    client_id: offlineClientId || data.selectedClient?.id || null,
    client_name: clientName,
    client_phone: clientPhone,
    client_address: clientAddress,
    client_document: clientDocument,
    status: data.status,
    document_type: data.documentType || 'orcamento',
    validity_days: parseInt(data.validityDays) || null,
    ...buildQuoteWarrantyColumns(data),
    ...buildQuoteHeadingColumns(data),
    ...buildQuoteExtraFieldsColumns(data),
    notes: data.notes || null,
    terms_conditions: data.termsConditions || null,
    // Frete + desconto de cabeçalho + total (que já os inclui). Sem estas
    // colunas o orçamento criado sem rede subiria SEM frete quando a rede
    // voltasse — insert bem-sucedido, sem erro de RLS, dinheiro a menos.
    ...buildQuoteTotalsColumns({ ...data, items }),
    // Idem para formas aceitas / condições / referência: quando a rede voltar, o
    // executor `create_quote` grava a partir DESTE payload, não do formulário.
    ...buildQuotePaymentColumns(data),
    ...buildQuotePdfTemplateColumns(data),
    title: data.title?.trim() || null,
    show_sequence_id: data.showSequenceId || false,
  };

  const itemsData = items.map((item, index) => ({
    id: item.id,
    name: item.name,
    description: item.description || null,
    quantity: item.quantity,
    unit_price: item.unit_price,
    // Snapshot do custo (products.cost do momento em que o item entrou). Precisa
    // atravessar a fila offline: o executor `create_quote` grava a partir DESTE
    // payload, não do catálogo — que pode ter outro custo quando a rede voltar.
    unit_cost: quoteItemUnitCost(item),
    discount: item.discount || 0,
    discount_type: item.discount_type || 'value',
    item_type: item.itemType || 'service',
    unit: item.unit || null,
    image_url: item.image_url || null,
    image_url_2: item.image_url_2 || null,
    image_url_3: item.image_url_3 || null,
    // Fonte única: a conta inline daqui IGNORAVA `discount_type`, então um item
    // com desconto percentual salvo offline gravava um total de linha errado (o
    // cabeçalho vinha certo, e a soma das linhas não batia com ele).
    total: quoteItemLineTotal(item),
    position: index,
    // Preserve saveToCategory flag + source/product_id for catalog items.
    saveToCategory: item.saveToCategory || false,
    source: item.source || 'manual',
    product_id: catalogItemOfflineMap.get(item.id) || item.product_id || null,
    barcode: item.barcode || null,
    // Vínculo com a despesa cobrável: um orçamento que JÁ tinha a despesa e é
    // reeditado offline precisa devolver a linha vinculada, senão o sync a
    // reinsere solta e a despesa vira intocável pelo financeiro. (CRIAR uma
    // despesa cobrável offline continua bloqueado — ver billableExpense.ts.)
    finance_entry_id: quoteItemFinanceEntryId(item),
  }));

  return saveQuoteOffline({
    quote: quoteData,
    items: itemsData,
    userId,
    companyId,
    countTowardQuoteQuota,
    generalPhotos: data.generalPhotos || [],
  }, opts);
}
